import { Routes, Route, Navigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { CommandPalette } from './CommandPalette';
import { HomePage } from '../pages/Home/HomePage';
import { TemplatesPage } from '../pages/Templates/TemplatesPage';
import { TemplateBuilderPage } from '../pages/Templates/TemplateBuilderPage';
import { TaskTemplatesPage } from '../pages/TaskTemplates/TaskTemplatesPage';
import { RoomsPage } from '../pages/Rooms/RoomsPage';
import { RoomDetailPage } from '../pages/Rooms/RoomDetailPage';
import { TeamsPage } from '../pages/Teams/TeamsPage';
import { OnboardingPage } from '../pages/Onboarding/OnboardingPage';
import { KnowledgeBasesPage } from '../pages/KnowledgeBases/KnowledgeBasesPage';
import { SharingPage } from '../pages/Sharing/SharingPage';
import { TasksPage } from '../pages/Tasks/TasksPage';
import { TaskDetailPage } from '../pages/Tasks/TaskDetailPage';
import { BacklogPage } from '../pages/Work/BacklogPage';
import { SprintsPage } from '../pages/Work/SprintsPage';
import { SprintPlanningPage } from '../pages/Work/SprintPlanningPage';
import { SprintBoardPage } from '../pages/Work/SprintBoardPage';
import { RetroPage } from '../pages/Work/RetroPage';
import { DailyScrumPage } from '../pages/Team/DailyScrumPage';
import { StandupPage } from '../pages/Team/StandupPage';
import { EstimationPage } from '../pages/Estimation/EstimationPage';
import { EstimateWizardPage } from '../pages/Estimation/EstimateWizardPage';
import { EstimateDetailPage } from '../pages/Estimation/EstimateDetailPage';
import { KnowledgePage } from '../pages/Knowledge/KnowledgePage';
import { ChatPage } from '../pages/Chat/ChatPage';
import { GoalsPage } from '../pages/Goals/GoalsPage';
import { GoalDetailPage } from '../pages/Goals/GoalDetailPage';
import { WorkstreamsPage } from '../pages/Workstreams/WorkstreamsPage';
import { WorkstreamDetailPage } from '../pages/Workstreams/WorkstreamDetailPage';
import { ChangeFeedPage } from '../pages/Changes/ChangeFeedPage';
import { DailyUpdatesPage } from '../pages/DailyUpdates/DailyUpdatesPage';
import { BlockersPage } from '../pages/Blockers/BlockersPage';
import { DocumentsPage } from '../pages/Documents/DocumentsPage';
import { DocumentDetailPage } from '../pages/Documents/DocumentDetailPage';
import { DecisionsPage } from '../pages/Decisions/DecisionsPage';
import { DecisionDetailPage } from '../pages/Decisions/DecisionDetailPage';
import { TimelinePage } from '../pages/Timeline/TimelinePage';
import { ServicesPage } from '../pages/Services/ServicesPage';
import { ServiceDetailPage } from '../pages/Services/ServiceDetailPage';
import { RecapPage } from '../pages/Recap/RecapPage';
import { AskVectorPage } from '../pages/AskVector/AskVectorPage';
import { ExpertsPage } from '../pages/Experts/ExpertsPage';
import { GraphPage } from '../pages/Graph/GraphPage';
import { SettingsPage } from '../pages/Settings/SettingsPage';
import { AboutPage } from '../pages/About/AboutPage';
import { AuthCallbackPage } from '../pages/Auth/AuthCallbackPage';
import { AuthLogoutPage } from '../pages/Auth/AuthLogoutPage';
import { AuthGate } from './AuthGate';
import { WorkspaceBootstrap } from './WorkspaceBootstrap';
import { TenantGate } from '../features/tenant/TenantGate';
import { LEGACY_REDIRECTS } from './consolidatedNav';

/**
 * App shell: fixed left Sidebar + top identity bar + routed content area.
 * Components reach the backend ONLY through the api facade (useApi) — never
 * fetch directly.
 */
export default function App() {
  return (
    <div className="min-h-screen bg-surface-0 text-zinc-200">
      <Sidebar />
      <div className="pl-60">
        <TopBar />
        <main className="mx-auto max-w-6xl px-6 py-8">
          <AuthGate>
            <TenantGate>
              <WorkspaceBootstrap />
              <CommandPalette />
              <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/graph" element={<GraphPage />} />
              <Route path="/goals" element={<GoalsPage />} />
              <Route path="/goals/:id" element={<GoalDetailPage />} />
              <Route path="/workstreams" element={<WorkstreamsPage />} />
              <Route path="/workstreams/:id" element={<WorkstreamDetailPage />} />
              <Route path="/changes" element={<ChangeFeedPage />} />
              <Route path="/daily-updates" element={<DailyUpdatesPage />} />
              <Route path="/blockers" element={<BlockersPage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/documents/:id" element={<DocumentDetailPage />} />
              <Route path="/decisions" element={<DecisionsPage />} />
              <Route path="/decisions/:id" element={<DecisionDetailPage />} />
              <Route path="/timeline" element={<TimelinePage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/:id" element={<ServiceDetailPage />} />
              <Route path="/recap" element={<RecapPage />} />
              <Route path="/ask" element={<AskVectorPage />} />
              <Route path="/experts" element={<ExpertsPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/templates/new" element={<TemplateBuilderPage />} />
              <Route path="/templates/:id" element={<TemplateBuilderPage />} />
              <Route path="/task-templates" element={<TaskTemplatesPage />} />
              <Route path="/rooms" element={<RoomsPage />} />
              <Route path="/rooms/:id" element={<RoomDetailPage />} />
              <Route path="/teams" element={<TeamsPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route
                path="/knowledge-bases"
                element={<KnowledgeBasesPage />}
              />
              <Route path="/sharing" element={<SharingPage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/tasks/:id" element={<TaskDetailPage />} />
              <Route path="/work/backlog" element={<BacklogPage />} />
              <Route path="/work/sprint" element={<SprintBoardPage />} />
              <Route path="/work/sprints" element={<SprintsPage />} />
              <Route
                path="/work/sprints/:id/plan"
                element={<SprintPlanningPage />}
              />
              <Route path="/team/daily-scrum" element={<DailyScrumPage />} />
              <Route path="/standup" element={<StandupPage />} />
              <Route path="/retro" element={<RetroPage />} />
              <Route path="/estimation" element={<EstimationPage />} />
              <Route path="/estimation/new" element={<EstimateWizardPage />} />
              <Route path="/estimation/:id" element={<EstimateDetailPage />} />
              <Route path="/knowledge" element={<KnowledgePage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route
                path="/global/auth/callback"
                element={<AuthCallbackPage />}
              />
              <Route path="/global/auth/logout" element={<AuthLogoutPage />} />
              {/* Backward-compatible redirects for the consolidated surfaces
                  (overview pages → Home tabs; Digest/Catch Me Up → Recap tabs).
                  Deep links / bookmarks resolve to the right tab, never 404. */}
              {LEGACY_REDIRECTS.map(({ from, to }) => (
                <Route
                  key={from}
                  path={from}
                  element={<Navigate to={to} replace />}
                />
              ))}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </TenantGate>
          </AuthGate>
        </main>
      </div>
    </div>
  );
}
